// src/components/FileBrowserToolbar.js
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus, faHammer, faMagnifyingGlass, faUpload } from '@fortawesome/free-solid-svg-icons';

import UploadFileModal from './UploadFileModal';
import ImportWizardModal from './ImportWizardModal';
import DuplicateManagerModal from './DuplicateManagerModal';
import ActionMenu from './common/ActionMenu';
import { COLUMN_LABELS, AVAILABLE_COLUMNS } from '../config/fileColumns';
import { SORTS, sortLabel } from '../config/fileSorts';
import { DENSITIES } from '../config/gridDensity';
import { WorkspaceContext } from '../context/WorkspaceContext';
import { fitToWidth, useElementWidth } from '../hooks/useOverflow';
import '../css/FileBrowserToolbar.css';

const EDIT_ROLES = ['editor', 'admin', 'owner'];

/**
 * Roughly how much room each labelled action needs, in order of how readily it
 * gives that room up. Upload is first because it is the one action that should
 * still be a button when the bar is squeezed; file maintenance never appears
 * here at all, because it starts in the overflow menu.
 */
const ACTION_WIDTHS = { upload: 104, import: 146, find: 140 };

/**
 * The library's command bar: what you can do here, regardless of what is
 * selected.
 *
 * Its contents are deliberately FIXED. This bar used to grow a second group of
 * eight controls the moment a file was selected, inside a fixed-height row that
 * couldn't wrap — so the actions ran off the end of a 1280px laptop and, under
 * the browser's `overflow: hidden`, simply could not be reached. Anything that
 * depends on a selection now lives in SelectionBar.
 *
 * The actions also carry visible labels. They were icon-only before, identified
 * only by a `title` tooltip — which never appears on a touch screen, and which
 * left a hammer standing for "file maintenance" and a magnifier standing for
 * "search the library by a file's CONTENT", right next to a text search box.
 */
const FileBrowserToolbar = ({
    onFilesChanged,
    viewMode,
    onViewModeChange,
    visibleColumns,
    onToggleColumn,
    onSearchByFile,
    sort,
    appliedSort,
    onSortChange,
    density,
    onDensityChange,
    filtersCollapsed = false,
    onToggleFilters,
}) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showColMenu, setShowColMenu] = useState(false);
    const colBtnRef = useRef(null);
    const searchFileRef = useRef(null);

    // The Columns menu could only be dismissed by clicking its backdrop, which
    // left keyboard users stuck inside it. Escape closes it and hands focus back
    // to the trigger.
    useEffect(() => {
        if (!showColMenu) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setShowColMenu(false);
                colBtnRef.current?.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [showColMenu]);

    const { activeWorkspaceRole } = useContext(WorkspaceContext);
    // Bulk import applies tags/metadata → mutation tier (editor+), matching the
    // backend's requireRole('editor') on POST /api/import/create.
    const canBulkImport = EDIT_ROLES.includes(activeWorkspaceRole);

    // When the bar runs out of room, actions move into the ⋯ menu rather than
    // off the end of it. The menu is always there, so nothing is ever stranded.
    const actionsRef = useRef(null);
    const actionsWidth = useElementWidth(actionsRef);
    const actions = [
        { id: 'upload', width: ACTION_WIDTHS.upload },
        canBulkImport && { id: 'import', width: ACTION_WIDTHS.import },
        { id: 'find', width: ACTION_WIDTHS.find },
    ].filter(Boolean);
    const { visible } = fitToWidth({ available: actionsWidth, items: actions });
    const shown = new Set(visible.map((item) => item.id));

    const pickSearchFile = () => searchFileRef.current?.click();

    const overflowItems = [
        !shown.has('import') && canBulkImport && {
            id: 'import',
            label: 'Import a folder',
            icon: <FontAwesomeIcon icon={faFolderPlus} />,
            onSelect: () => setShowImportModal(true),
        },
        !shown.has('find') && {
            id: 'find',
            label: 'Find matches',
            description: 'Search by a file’s content — it is not uploaded',
            icon: <FontAwesomeIcon icon={faMagnifyingGlass} />,
            onSelect: pickSearchFile,
        },
        {
            id: 'maintenance',
            label: 'File maintenance',
            description: 'Find duplicates and files that need attention',
            icon: <FontAwesomeIcon icon={faHammer} />,
            separatorBefore: true,
            onSelect: () => setShowDuplicateModal(true),
        },
    ].filter(Boolean);

    return (
        <>
            <div className="fb-toolbar" role="group" aria-label="Library actions">
                {/* Sits at the far left, over the rail it controls. Labelled,
                    because once the rail is hidden there is nothing else on
                    screen to say where the filters went. */}
                {onToggleFilters && (
                    <button
                        type="button"
                        className="qd-btn qd-btn--ghost qd-btn--sm fb-filters-toggle"
                        onClick={onToggleFilters}
                        aria-expanded={!filtersCollapsed}
                        aria-controls="qd-filter-rail"
                        title={filtersCollapsed ? 'Show the filter panel' : 'Hide the filter panel'}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" />
                        </svg>
                        Filters
                    </button>
                )}

                <div className="fb-toolbar-group" ref={actionsRef}>
                    <button
                        type="button"
                        className="qd-btn qd-btn--primary qd-btn--sm"
                        onClick={() => setShowUploadModal(true)}
                    >
                        <FontAwesomeIcon icon={faUpload} /> Upload
                    </button>

                    {canBulkImport && shown.has('import') && (
                        <button
                            type="button"
                            className="qd-btn qd-btn--secondary qd-btn--sm"
                            onClick={() => setShowImportModal(true)}
                        >
                            <FontAwesomeIcon icon={faFolderPlus} /> Import folder
                        </button>
                    )}

                    {/* Search the library BY a file's content. A button as well as
                        a drag target, because a drag gesture is undiscoverable,
                        unreachable by keyboard, and — as this feature learned the
                        hard way — easy to get wrong in a way that silently
                        uploads the file instead. */}
                    {shown.has('find') && (
                        <button
                            type="button"
                            className="qd-btn qd-btn--secondary qd-btn--sm"
                            onClick={pickSearchFile}
                            title="Pick a file to search by its content — it is not uploaded"
                        >
                            <FontAwesomeIcon icon={faMagnifyingGlass} /> Find matches
                        </button>
                    )}
                    <input
                        ref={searchFileRef}
                        type="file"
                        hidden
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            // Reset first: picking the SAME file twice fires no
                            // change event otherwise, so the second attempt
                            // would silently do nothing.
                            e.target.value = '';
                            if (file) onSearchByFile?.(file);
                        }}
                    />

                    <ActionMenu
                        label="More library tools"
                        align="start"
                        triggerClassName="qd-btn qd-btn--ghost qd-btn--sm qd-btn--icon"
                        items={overflowItems}
                    />
                </div>

                <div className="fb-toolbar-group fb-toolbar-group--end">
                    {/* The requested sort isn't always the one in force: the
                        backend serves relevance order (and says so) until the
                        sort replicas exist. Showing the request would be a lie. */}
                    <ActionMenu
                        label="Sort results"
                        focusOnOpen="checked"
                        triggerClassName="qd-btn qd-btn--ghost qd-btn--sm fb-sort-trigger"
                        triggerAriaLabel={`Sort results: ${sortLabel(sort)}`}
                        trigger={(
                            <>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M7 4v16m0 0l-3-3m3 3l3-3" /><path d="M17 20V4m0 0l-3 3m3-3l3 3" />
                                </svg>
                                <span className="fb-sort-label">{sortLabel(sort)}</span>
                                {appliedSort && appliedSort !== sort && (
                                    <span className="fb-sort-warn" title="Sorting isn't available yet — showing best match. Run the Algolia settings step.">!</span>
                                )}
                            </>
                        )}
                        items={SORTS.map((option) => ({
                            id: option.id,
                            label: option.label,
                            description: option.description,
                            checked: option.id === sort,
                            onSelect: () => onSortChange?.(option.id),
                        }))}
                    />

                    {viewMode === 'grid' && (
                        <ActionMenu
                            label="Card size"
                            focusOnOpen="checked"
                            triggerClassName="qd-btn qd-btn--ghost qd-btn--sm qd-btn--icon"
                            triggerAriaLabel={`Card size: ${DENSITIES[density]?.label ?? ''}`}
                            trigger={(
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="3" y="3" width="8" height="8" /><rect x="14" y="3" width="7" height="4" />
                                    <rect x="14" y="10" width="7" height="4" /><rect x="3" y="14" width="8" height="7" />
                                    <rect x="14" y="17" width="7" height="4" />
                                </svg>
                            )}
                            items={Object.values(DENSITIES).map((option) => ({
                                id: option.id,
                                label: option.label,
                                checked: option.id === density,
                                onSelect: () => onDensityChange?.(option.id),
                            }))}
                        />
                    )}

                    {viewMode === 'table' && (
                        <div className="dropdown-wrapper">
                            <button
                                ref={colBtnRef}
                                type="button"
                                className="icon-btn"
                                onClick={() => setShowColMenu((v) => !v)}
                                aria-haspopup="true"
                                aria-expanded={showColMenu}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                     stroke="currentColor" strokeWidth="2"
                                     strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M12 3h7a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-7m0-18H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7m0-18v18" />
                                </svg>
                                <span>Columns</span>
                            </button>
                            {showColMenu && (
                                <>
                                    <div className="dropdown-backdrop" onClick={() => setShowColMenu(false)} />
                                    <div className="dropdown-menu">
                                        <h4>Visible columns</h4>
                                        {AVAILABLE_COLUMNS.map((col) => (
                                            <label key={col} className="dropdown-item">
                                                <input
                                                    type="checkbox"
                                                    checked={visibleColumns.includes(col)}
                                                    onChange={() => onToggleColumn(col)}
                                                />
                                                {COLUMN_LABELS[col]}
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <div className="view-toggle" role="group" aria-label="View mode">
                        <button
                            type="button"
                            className={`vt-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('grid')}
                            aria-pressed={viewMode === 'grid'}
                            aria-label="Grid view"
                            title="Grid view"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                                <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`vt-btn ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => onViewModeChange('table')}
                            aria-pressed={viewMode === 'table'}
                            aria-label="Table view"
                            title="Table view"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
                                <line x1="8" y1="18" x2="21" y2="18" />
                                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" />
                                <line x1="3" y1="18" x2="3.01" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {showUploadModal && createPortal(
                <UploadFileModal onFilesChanged={onFilesChanged} onClose={() => setShowUploadModal(false)} />,
                document.body,
            )}
            {showImportModal && createPortal(
                <ImportWizardModal onFilesChanged={onFilesChanged} onClose={() => setShowImportModal(false)} />,
                document.body,
            )}
            {showDuplicateModal && createPortal(
                <DuplicateManagerModal onClose={() => { setShowDuplicateModal(false); onFilesChanged?.(); }} />,
                document.body,
            )}
        </>
    );
};

export default FileBrowserToolbar;
